import { cn } from 'lib/utils'

import { STAGE_HEIGHT, STAGE_WIDTH, themeOf } from '../../layouts'
import type { AssetOption, Story } from '../../types/presentation.types'
import { SCOPE_OPTIONS } from '../AssetPicker'

/**
 * The generated slide that opens a story when it asks for one (MYD-497): the
 * asset's photo, name and address, and where the story sits in the deck.
 *
 * Pure, like SlideRenderer, and drawn on the same 1280x720 stage, so it
 * scales, flips and prints exactly like a real slide. The asset's details are
 * looked up by the caller (they are never stored in the deck, so they cannot
 * go stale) and may be missing: an asset the viewer cannot see, or one with
 * no photo, still gets a clean divider.
 */
interface StoryDividerSlideProps {
	story: Story
	storyNumber: number
	storyCount: number
	/** The anchor's details, or null while loading or when not available. */
	asset: AssetOption | null
	theme?: string
}

export function StoryDividerSlide({ story, storyNumber, storyCount, asset, theme }: StoryDividerSlideProps) {
	const colors = themeOf(theme)
	const photo = asset?.image || ''
	const scope = SCOPE_OPTIONS.find(option => option.scope === story.anchor?.scope)
	const heading = asset?.name || story.title || `Story ${storyNumber}`
	// The story title is shown separately only when it says something the
	// asset name does not.
	const subheading = story.title && story.title !== heading ? story.title : ''

	return (
		<div
			className={cn('relative overflow-hidden', photo ? 'bg-gray-900 text-white' : colors.stage)}
			style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT }}
			data-layout="story-divider"
		>
			{photo && (
				<>
					<img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
					<div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
				</>
			)}

			<div className="relative flex h-full flex-col justify-end px-20 pb-20">
				<p className={cn('mb-4 text-[22px] font-medium uppercase tracking-[0.2em]', photo ? 'text-white/70' : colors.muted)}>
					{scope ? `${scope.label} · ` : ''}Story {storyNumber} of {storyCount}
				</p>
				<h1 className={cn('text-[80px] font-bold leading-[1.05]', photo ? 'text-white' : colors.title)}>{heading}</h1>
				{subheading && <p className={cn('mt-4 text-[32px]', photo ? 'text-white/85' : colors.subtitle)}>{subheading}</p>}
				{asset?.address && <p className={cn('mt-6 text-[26px]', photo ? 'text-white/75' : colors.muted)}>{asset.address}</p>}
				{!photo && <div className="mt-10 h-1.5 w-28 rounded-full bg-primary" />}
			</div>
		</div>
	)
}

export default StoryDividerSlide
